/*
 * Copyright ©2020. Open Digital Solutions, Novi Sad. Sva prava zadržana.
 * Pravo da se koristi, kopira, modifikuje i distribuira ovaj softver i njegova dokumentacija
 * u bilo koje svrhe, bez naknade ili bez potpisanog sporazuma sa vlasnikom softvera, nije dozvoljeno.
 */

import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { Component, Inject, Optional, ViewEncapsulation } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { MatChipInputEvent } from '@angular/material/chips';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { Subject } from 'src/models/subject.model';
import { CodebookService } from 'src/services/codebook.service';
import { ToastrImplService } from 'src/services/toastr.service';
import { ModalEventEnum } from 'src/types';
import { PrintComponent } from '../components/print/print.component';
import { LocalStorageService } from '../local-storage.service';
import { Page } from '../page';
import { SubjectsService } from './../../services/subjects.service';
import { UserService } from 'src/services/user.service';
import { AssigneDossierComponent } from './assigne-dossier/assigne-dossier.component';
import { CpsuSubjectService } from 'src/services/cpsu-subject.service';
import { ServiceProviderSubjectService } from 'src/services/service-provider-subject.service';
import { ServiceProvidersService } from 'src/services/service-providers.service';

export interface SearchItem {
   name: string;
}

enum AutocompleteFilterEnum {
   SUBCENTER = 'subcenter',
}

interface SelectOptions {
   value: any;
   viewValue: string;
}

@Component({
   selector: 'app-subjects',
   templateUrl: './subjects.component.html',
   styleUrls: ['./subjects.component.scss'],
   encapsulation: ViewEncapsulation.None,
})
export class SubjectsComponent {
   selectable = true;
   removable = true;
   readonly separatorKeysCodes: number[] = [COMMA, ENTER];
   public searchTerms: string[] = [];

   public searchItems: SearchItem[] = [];
   page: Page<Subject> = new Page();
   object: { origin: string };
   isUserManager = false;
   areLoaded = false;
   subcenter = new FormControl();
   subcenterCsrId: any;
   subcenters = [];
   loggedIn: any;
   isMainCenterBelgrade = false;
   isUserDrugostepeni = false;
   isCpsuUser = false;
   isServiceProviderUser = false;
   isGlobalAdmin = false;
   isAssociate = false;
   roleLength: any;
   allCsrCodebooks = [];
   filteredSubcenters: Observable<any>;

   getFirstPageEvent = 0;
   getPreviousPageEvent = 0;
   getNextPageEvent = 0;
   getLastPageEvent = 0;
   getPageInNewSizeEvent = 10;
   applyFilterEvent: any = [];
   changeCenterEvent: any;
   isCpsuCenter = false;
   serviceProviderId: any;
   providedServices: any[] = [];

   filterForm: FormGroup;
   filterInUse = false;
   statusOptions: SelectOptions[] = [
      { value: 'ALL', viewValue: 'СВИ СТАТУСИ' },
      { value: 'ACTIVE', viewValue: 'АКТИВАН' },
      { value: 'PASSIVE', viewValue: 'ПАСИВАН' },
      { value: 'UNDEFINED', viewValue: 'НЕДЕФИНИСАН' },
   ];
   constructor(
      private subjectsService: SubjectsService,
      public dialog: MatDialog,
      @Optional() @Inject(MAT_DIALOG_DATA) public data: any,
      @Optional() public dialogRef: MatDialogRef<SubjectsComponent>,
      public router: Router,
      private translate: TranslateService,
      private codebookService: CodebookService,
      private localStorageService: LocalStorageService,
      private toastr: ToastrImplService,
      private userService: UserService,
      private cpsuSubjectService: CpsuSubjectService,
      private serviceProviderSubjectService: ServiceProviderSubjectService,
      private formBuilder: FormBuilder,
      private route: ActivatedRoute,
      private serviceProvidersService: ServiceProvidersService,
   ) {
      this.filterForm = this.formBuilder.group({
         status: ['ALL'],
         address: [''],
         providedServices: [null],
      });

      this.localStorageService.remove('case');
      this.loggedIn = JSON.parse(this.localStorageService.get('loggedUser'));
      this.isUserDrugostepeni = this.userService.isUserDrugostepeni();
      this.isMainCenterBelgrade = this.loggedIn?.csrId === '1' ? true : false;
      this.isGlobalAdmin = this.userService.isGlobalAdmin();
      this.roleLength = JSON.parse(this.localStorageService.get('loggedUser')).role.length;
      this.isAssociate = this.roleLength === 1 ? this.userService.isAssociate() : this.localStorageService.get('primaryRole') === ' Сарадник';

      this.isCpsuUser = this.loggedIn.isCpsu;
      this.isServiceProviderUser = this.loggedIn.isServiceProviderUser;
      this.serviceProviderId = this.loggedIn?.serviceProviderId;

      this.object = { ...data };

      if (this.isUserDrugostepeni || this.isMainCenterBelgrade || this.isGlobalAdmin) {
         if (this.localStorageService.get('localStorageSubcenter') !== null) {
            const localStorageSubcenter = JSON.parse(this.localStorageService.get('localStorageSubcenter'));
            this.subcenterCsrId = localStorageSubcenter.id;
            this.isCpsuCenter = localStorageSubcenter.isCpsu;
            this.subcenter.setValue(localStorageSubcenter);
            this.changeCenter(localStorageSubcenter);
         } else {
            this.subcenterCsrId = 0;
         }

         if (this.isUserDrugostepeni) {
            // Drugostepeni - svi centri se dobavljaju
            this.codebookService.getCsrCodebook().subscribe(result => {
               this.subcenters = result;
               this.filteredSubcenters = this.subcenter.valueChanges.pipe(
                  startWith(''),
                  map(value => (typeof value === 'string' ? value : value.name)),
                  map(title => (title ? this._filter(title, AutocompleteFilterEnum.SUBCENTER) : this.subcenters.slice())),
               );
            });
         } else {
            // Glavni centar Beograd - podcentri
            this.codebookService.getAllSubcenters().subscribe(result => {
               this.subcenters = result;
               this.filteredSubcenters = this.subcenter.valueChanges.pipe(
                  startWith(''),
                  map(value => (typeof value === 'string' ? value : value.name)),
                  map(title => (title ? this._filter(title, AutocompleteFilterEnum.SUBCENTER) : this.subcenters.slice())),
               );
            });
         }
      } else {
         this.subcenterCsrId = this.loggedIn?.csrId;
      }
      this.getQueryParams();
      if (this.isServiceProviderUser) {
         this.serviceProvidersService.findProvidedServicesByServiceProviderId(this.serviceProviderId).subscribe(res => {
            this.providedServices = res;
         });
      }
   }

   public printPDF(): void {
      if (!this.isCpsuUser && !this.isCpsuCenter && !this.isServiceProviderUser) {
         let centerId = null;
         if (this.isMainCenterBelgrade || this.isUserDrugostepeni || this.isGlobalAdmin) {
            if (this.subcenterCsrId !== undefined && this.subcenterCsrId !== null && this.subcenterCsrId !== 0) {
               centerId = this.subcenterCsrId;
            } else {
               this.toastr.info('SUBJECTS.WARMING_CENTAR');
            }
         } else {
            centerId = this.loggedIn?.csrId;
         }

         if (centerId !== null) {
            const dialogRef = this.dialog.open(PrintComponent, {
               panelClass: 'overlay-panel',
               disableClose: true,
               width: '45%',
               data: {
                  maxNumber: this.page.totalElements,
                  whole: null,
                  partial: {
                     from: null,
                     to: null,
                     type: null,
                  },
                  src: 's',
                  csr: centerId,
               },
            });

            dialogRef.afterClosed().subscribe(res => {
               if (res.event === ModalEventEnum.SUCCESS) {
                  const printDto = {
                     csrId: centerId,
                     whole: res.data.whole,
                     from: res.data.partial.from,
                     to: res.data.partial.to,
                     type: res.data.partial.type,
                  };
                  this.subjectsService.printPdf(printDto);
               }
            });
         }
      } else if (this.isCpsuCenter || this.isCpsuUser) {
         const body = {
            pageId: this.page.pageable.pageNumber,
            sizeId: this.page.pageable.pageSize,
            csrId: this.isMainCenterBelgrade || this.isUserDrugostepeni || this.isGlobalAdmin ? this.subcenterCsrId : this.loggedIn?.csrId,
            searchData: this.searchTerms,
            status: this.filterInUse ? this.filterForm.value.status : null,
            address: this.filterInUse && this.filterForm.value.address != '' ? this.filterForm.value.address : null,
         };
         this.cpsuSubjectService.printPdf(body);
      } else {
         const body = {
            pageId: this.page.pageable.pageNumber,
            sizeId: this.page.pageable.pageSize,
            serviceProviderId: this.serviceProviderId,
            searchData: this.searchTerms,
         };
         this.serviceProviderSubjectService.printPdf(body);
      }
   }

   public exportCsv(): void {
      let filename = '';
      this.translate.get('SUBJECTS.TITLE').subscribe((res: string) => {
         filename = res;
      });
      if (this.isServiceProviderUser) {
         const body = {
            pageId: this.page.pageable.pageNumber,
            sizeId: this.page.pageable.pageSize,
            serviceProviderId: this.serviceProviderId,
            searchData: this.searchTerms,
         };
         this.serviceProviderSubjectService.exportCSV(body, filename);
      } else if (!((this.isMainCenterBelgrade || this.isUserDrugostepeni || this.isGlobalAdmin) && this.subcenterCsrId === undefined)) {
         const body = {
            pageId: this.page.pageable.pageNumber,
            sizeId: this.page.pageable.pageSize,
            csrId: this.isMainCenterBelgrade || this.isUserDrugostepeni ? this.subcenterCsrId : this.loggedIn?.csrId,
            searchData: this.searchTerms,
            status: this.filterInUse ? this.filterForm.value.status : null,
            address: this.filterInUse && this.filterForm.value.address != '' ? this.filterForm.value.address : null,
         };
         if (this.isCpsuUser || this.isCpsuCenter) {
            this.cpsuSubjectService.exportCSV(body, filename);
         } else {
            this.subjectsService.exportCSV(body, filename);
         }
      } else {
         this.toastr.info('SUBJECTS.WARMING_CENTAR');
      }
   }

   private _filter(value: string, what: AutocompleteFilterEnum): any[] {
      const filterValue = value.toLowerCase();

      switch (what) {
         case AutocompleteFilterEnum.SUBCENTER:
            return this.subcenters.filter(option => option.name.toLowerCase().includes(filterValue));
         default:
            break;
      }
   }
   displayCustomFormat(objectValue: any): string {
      return objectValue ? objectValue.name : objectValue;
   }

   add(event: MatChipInputEvent): void {
      const input = event.input;
      const value = event.value;
      // Add new search term
      if ((value || '').trim()) {
         this.searchItems.push({ name: value.trim() });
      }
      // Reset the input value
      if (input) {
         input.value = '';
      }
      this.searchTerms = this.searchItems.map(function (searchItem) {
         return searchItem.name;
      });
      this.applyFilterEvent = this.searchTerms;
   }

   remove(item: SearchItem): void {
      const index = this.searchItems.indexOf(item);
      if (index >= 0) {
         this.searchItems.splice(index, 1);
         this.searchTerms = this.searchItems.map(function (searchItem) {
            return searchItem.name;
         });
         this.applyFilterEvent = this.searchTerms;
      }
   }

   loadingChangeStatus(event: boolean): void {
      this.areLoaded = event;
   }

   getFirstPage() {
      this.getFirstPageEvent = Date.now();
   }

   getPreviousPage() {
      this.getPreviousPageEvent = Date.now();
   }

   getNextPage() {
      this.getNextPageEvent = Date.now();
   }

   getLastPage() {
      this.getLastPageEvent = Date.now();
   }

   getPageInNewSize(event: any) {
      this.getPageInNewSizeEvent = event;
   }

   pageDataChanged(page: Page<Subject>) {
      this.page = page;
   }

   changeCenter(event: any) {
      this.localStorageService.set('localStorageSubcenter', JSON.stringify(event));
      this.subcenterCsrId = event?.id;
      this.isCpsuCenter = event.isCpsu;
      this.changeCenterEvent = event;
   }

   rowClickedEvent(element: any) {
      if (this.object.origin === 'entrance') {
         this.dialogRef.close({ data: element });
      } else if (this.object.origin === 'accommodation') {
         this.dialogRef.close({ data: element });
      } else if (this.object.origin === 'nsp') {
         this.dialogRef.close({ data: element });
      } else if (this.object.origin === 'duty-proceedings-initiation') {
         this.dialogRef.close({ data: element });
      } else if (this.object.origin === 'forms23') {
         if (this.data.members.some((member: any) => member.subjectIdentity.csrId === element.subjectIdentity.csrId && member.subjectIdentity.subjectId === element.subjectIdentity.subjectId)) {
            this.toastr.error('SNACKBAR.ALREADY_MEMBER_OF_FAMILY');
         } else {
            this.dialogRef.close({ data: element });
         }
      } else {
         this.localStorageService.set('localStorageCenterId', this.subcenterCsrId);
         if (this.isCpsuUser || this.isCpsuCenter) {
            this.router.navigate(['/subjects/cpsu/', element.id]);
         } else if (this.isServiceProviderUser) {
            this.router.navigate(['/subjects/service-providers/', element.id]);
         } else {
            this.router.navigate(['/subjects/', element.subjectIdentity.subjectId]);
         }
      }
   }

   handleOpenDialog(element: any) {
      const data = {
         isCpsuUser: this.isCpsuUser,
         cpsuSubjectId: !this.isCpsuUser ? null : element.id,
         subjectIdentity: this.isCpsuUser || this.isServiceProviderUser ? null : element.subjectIdentity,
         isServiceProviderUser: this.isServiceProviderUser,
         serviceProviderSubjectId: !this.isServiceProviderUser ? null : element.id,
         serviceProviderId: this.serviceProviderId,
      };
      const dialogRef = this.dialog.open(AssigneDossierComponent, {
         panelClass: 'overlay-panel',
         width: '800px',
         data: data,
      });
      dialogRef.afterClosed().subscribe(
         result => {
            if (result.event === 'Cancel') {
               this.toastr.info('SNACKBAR.CANCEL_BORROWING_DOSSIER');
            } else {
               this.toastr.success('SNACKBAR.BORROWED_DOSSIER');
            }
         },
         error => {
            console.log('Дошло је до грешке');
         },
      );
   }

   resetFilter() {
      this.filterInUse = false;
      this.filterForm.controls.status.setValue('ALL');
      this.filterForm.controls.address.setValue('');
      this.filterForm.controls.providedServices.setValue(null);
      this.getFirstPage();
   }

   compareLoadedObjects(object1: any, object2: any) {
      return object1 && object2 && object1 === object2;
   }

   applyCustomFilter() {
      this.filterInUse = true;
      this.page.pageable.pageNumber = 0;
      this.getFirstPage();
   }

   getQueryParams() {
      this.route.queryParams.subscribe(param => {
         if (param?.filter) {
            this.searchItems = [];
            const globalFilter = Array.isArray(param.filter) ? param.filter : [param.filter];
            for (const filter of globalFilter) {
               this.searchItems.push({ name: filter });
            }
         }

         if (param?.status) {
            this.filterInUse = true;
            this.filterForm.controls.status.setValue(param.status);
         }

         if (param?.address) {
            this.filterInUse = true;
            this.filterForm.controls.address.setValue(param.address);
         }
      });
   }
}
